import * as Select from '@radix-ui/react-select';
import { clsx } from 'clsx';
import { forwardRef, useState, useCallback } from 'react';
import { Icon } from '@repo/ui/Icon';
import s from './Dropdown.module.css';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Dropdown component built on Radix UI Select
 *
 * @example
 * ```tsx
 * <Dropdown
 *   id="country"
 *   label="Country"
 *   options={[
 *     { value: 'us', label: 'United States' },
 *     { value: 'ca', label: 'Canada' }
 *   ]}
 *   required
 * />
 * ```
 */
export interface DropdownProps {
  options: DropdownOption[];
  placeholder?: string;
  label?: string;
  labelVariant?: 'above' | 'embedded';
  className?: string;
  id?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  'data-testid'?: string;
  required?: boolean;
  /** Controlled value - use for controlled component */
  value?: string;
  /** Default value - use for uncontrolled component */
  defaultValue?: string;
  /**
   * Radix UI callback for value changes
   * Preferred for Radix semantics
   */
  onValueChange?: (value: string) => void;
  /**
   * React Hook Form compatible callback
   * Includes event shape matching native select
   * Requires `name` prop to be set
   */
  onChange?: (event: {
    target: { value: string; name?: string; type?: string };
  }) => void;
  /** Blur callback for React Hook Form touched state */
  onBlur?: () => void;
  disabled?: boolean;
  /** Required for React Hook Form integration */
  name?: string;
  /** Display loading state with aria-busy and optionally disable interaction */
  loading?: boolean;
  /** Error message to display below the dropdown */
  error?: string;
  /** Override the auto-generated error message ID (for custom aria-describedby composition) */
  errorId?: string;
  /**
   * Custom render function for the selected value
   * Useful for displaying icons or formatted text
   */
  renderValue?: (value: string) => React.ReactNode;
  /**
   * Custom render function for dropdown items
   * Allows rich content like icons, badges, or multi-line text
   */
  renderItem?: (option: DropdownOption) => React.ReactNode;
  /** Callback when dropdown opens/closes - useful for analytics */
  onOpenChange?: (open: boolean) => void;
}

export const Dropdown = forwardRef<HTMLButtonElement, DropdownProps>(
  (props, ref) => {
    const {
      options,
      placeholder = 'Select an option',
      label,
      labelVariant = 'embedded',
      className,
      id,
      required,
      value,
      defaultValue,
      onValueChange,
      onChange,
      onBlur,
      disabled,
      name,
      loading,
      error,
      errorId: errorIdProp,
      renderValue,
      renderItem,
      onOpenChange,
      'data-testid': dataTestId,
      'aria-labelledby': ariaLabelledBy,
      'aria-label': ariaLabel,
    } = props;

    // Note: onChange requires name prop for React Hook Form integration

    // Generate stable IDs for accessibility
    const embeddedId = id ? `${id}-embedded-label` : undefined;
    const aboveLabelId = id ? `${id}-above-label` : undefined;
    // Allow errorId override for custom aria-describedby composition
    const errorId = errorIdProp || (id && error ? `${id}-error` : undefined);

    // Controlled vs uncontrolled guard
    const isControlled = value !== undefined;

    // Track internal value for uncontrolled + renderValue support
    const [internalValue, setInternalValue] = useState<string | undefined>(
      defaultValue,
    );
    const currentValue = isControlled ? value : internalValue;

    // Find selected option for custom rendering
    const selectedOption = options.find((opt) => opt.value === currentValue);

    // Merge external ref (from forwardRef) with any internal ref needs
    const mergedRef = useCallback(
      (node: HTMLButtonElement | null) => {
        // Call the forwardRef
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const wrapperClasses = clsx(
      s.wrapper,
      {
        [s.withAboveLabel]: labelVariant === 'above' && label,
        [s.withEmbeddedLabel]: labelVariant === 'embedded' && label,
      },
      className,
    );

    // Handle both Radix's onValueChange and react-hook-form's onChange
    const handleValueChange = (newValue: string) => {
      // Update internal state for uncontrolled components
      if (!isControlled) {
        setInternalValue(newValue);
      }

      onValueChange?.(newValue);

      // Create a more complete synthetic event for react-hook-form compatibility
      if (onChange) {
        const syntheticEvent = {
          target: {
            value: newValue,
            name,
            type: 'select-one',
          },
          currentTarget: {
            value: newValue,
            name,
            type: 'select-one',
          },
          // Add type property to satisfy ChangeEvent type checking
          type: 'change',
        } as unknown as React.ChangeEvent<HTMLSelectElement>;

        onChange(syntheticEvent);
      }
    };

    return (
      <div className={wrapperClasses}>
        {labelVariant === 'above' && label && (
          <span id={aboveLabelId} className={s.aboveLabel}>
            {label}
            {required && <span aria-hidden="true"> *</span>}
          </span>
        )}
        <Select.Root
          value={isControlled ? value : undefined}
          defaultValue={!isControlled ? defaultValue : undefined}
          onValueChange={handleValueChange}
          onOpenChange={onOpenChange}
          disabled={disabled || loading}
          name={name}
          required={required}
        >
          <Select.Trigger
            ref={mergedRef}
            className={clsx(s.trigger, error && s.triggerError)}
            id={id}
            data-testid={dataTestId}
            aria-labelledby={clsx(ariaLabelledBy, aboveLabelId, embeddedId)}
            aria-label={ariaLabel}
            aria-required={required}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={errorId}
            aria-busy={loading}
            onBlur={onBlur}
          >
            {renderValue && selectedOption ? (
              <span className={s.customValue}>
                {renderValue(selectedOption.value)}
              </span>
            ) : (
              <Select.Value placeholder={placeholder} />
            )}
            <Select.Icon className={s.icon}>
              <Icon name="line-select" aria-hidden="true" />
            </Select.Icon>
            {labelVariant === 'embedded' && label && (
              <span id={embeddedId} className={s.embeddedLabel}>
                {label}
                {required && <span aria-hidden="true"> *</span>}
              </span>
            )}
          </Select.Trigger>

          <Select.Portal>
            <Select.Content
              className={clsx(s.content, error && s.contentError)}
              position="popper"
              sideOffset={4}
              align="start"
              aria-labelledby={clsx(ariaLabelledBy, aboveLabelId, embeddedId)}
            >
              <Select.ScrollUpButton
                className={s.scrollButton}
                aria-label="Scroll up"
              >
                <Icon name="line-select" aria-hidden="true" />
              </Select.ScrollUpButton>
              <Select.Viewport className={s.viewport}>
                {options.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={s.item}
                  >
                    <Select.ItemText>
                      {renderItem ? renderItem(option) : option.label}
                    </Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
              <Select.ScrollDownButton
                className={s.scrollButton}
                aria-label="Scroll down"
              >
                <Icon name="line-select" aria-hidden="true" />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
        {error && (
          <div id={errorId} className={s.error} role="alert">
            {error}
          </div>
        )}
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';
